import React from "react"
import { GroundOverlay, Polygon } from "@react-google-maps/api"

import type * as Models from "@/redux/models"
import { heatmapCellColor } from "./heatmap-colors"
import { getHeatmapRaster } from "./heatmap-raster"

const M_PER_DEG_LAT = 111_320
const GRID_STROKE_OPACITY = 0.1

function cellPath(
  lat: number,
  lng: number,
  resolutionM: number,
): google.maps.LatLngLiteral[] {
  const halfLat = resolutionM / (2 * M_PER_DEG_LAT)
  const halfLng =
    resolutionM / (2 * M_PER_DEG_LAT * Math.cos(lat * (Math.PI / 180)))
  return [
    { lat: lat - halfLat, lng: lng - halfLng },
    { lat: lat - halfLat, lng: lng + halfLng },
    { lat: lat + halfLat, lng: lng + halfLng },
    { lat: lat + halfLat, lng: lng - halfLng },
  ]
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
  )
}

export interface IrrigationHeatmapLayerProps {
  grid: Models.IrrigationHeatmapGrid
  /** See IrrigationHeatmapLayer.tsx — history density view opts out. */
  preferRaster?: boolean
}

/**
 * Web version of the field irrigation heatmap. Preferred path: one
 * `GroundOverlay` per field showing the server-rendered raster; fallback:
 * the legacy per-cell polygon grid. See IrrigationHeatmapLayer.tsx.
 */
export function IrrigationHeatmapLayer({
  grid,
  preferRaster = true,
}: IrrigationHeatmapLayerProps): React.JSX.Element {
  const raster = preferRaster ? getHeatmapRaster(grid) : null

  if (raster) {
    return <GroundOverlay bounds={raster.bounds} url={raster.rasterUrl} />
  }

  return (
    <React.Fragment>
      {grid.cells.map((cell, i) => {
        const { r, g, b, a } = heatmapCellColor(cell.accumulatedMm)
        const hexColor = rgbToHex(r, g, b)
        const path = cellPath(cell.lat, cell.lng, grid.resolutionM)
        return (
          <Polygon
            key={i}
            options={{
              fillColor: hexColor,
              fillOpacity: a,
              strokeColor: hexColor,
              strokeOpacity: GRID_STROKE_OPACITY,
              strokeWeight: 0.5,
              // Base layer — see PivotIrrigationHeatmapLayer.web.tsx
              // for the rationale. Without this, the heatmap cells
              // can render above unindexed control-overlay shapes.
              zIndex: 0,
            }}
            path={path}
          />
        )
      })}
    </React.Fragment>
  )
}
